import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ApiGet, ApiPatch } from "../../../helpers/API/ApiData";
import { toast } from "react-toastify";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "skipped", label: "Skipped" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const toDisplayStatus = (value = "") =>
  String(value || "not_started")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const isLikelyUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());

const resolveErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  return (
    error?.message ||
    error?.error?.message ||
    error?.response?.data?.message ||
    error?.data?.message ||
    fallback
  );
};

const EnterpriseVerifications = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const query = new URLSearchParams();
      if (statusFilter !== "all") query.set("status", statusFilter);
      if (search.trim()) query.set("search", search.trim());
      query.set("limit", "300");
      const res = await ApiGet(`admin/verifications?${query.toString()}`);
      const nextRows = res?.data?.payload?.records || [];
      setRows(nextRows);
    } catch (err) {
      setRows([]);
      setError(resolveErrorMessage(err, "Unable to load verification requests."));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const loadDetail = useCallback(
    async (verificationId) => {
      if (!verificationId) {
        setSelectedRecord(null);
        setSelectedId("");
        return;
      }
      setDetailLoading(true);
      setError("");
      try {
        const res = await ApiGet(`admin/verifications/${verificationId}`);
        const nextRecord = res?.data?.payload?.verification || null;
        setSelectedRecord(nextRecord);
        setSelectedId(verificationId);
        setRejectionReason(nextRecord?.review?.rejectionReason || "");
      } catch (err) {
        setError(resolveErrorMessage(err, "Unable to load verification detail."));
        setSelectedRecord(null);
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      loadRows();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadRows]);

  const handleAction = async (action) => {
    if (!selectedId || !selectedRecord) return;
    if (selectedRecord?.status !== "submitted") {
      toast.error("Only submitted records can be approved or rejected.");
      return;
    }
    if (action === "reject" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }

    setActionLoading(true);
    setError("");
    try {
      if (action === "approve") {
        await ApiPatch(`admin/verifications/${selectedId}/approve`, {});
        toast.success("Verification approved.");
      } else {
        await ApiPatch(`admin/verifications/${selectedId}/reject`, {
          rejectionReason: rejectionReason.trim(),
        });
        toast.success("Verification rejected.");
      }
      await Promise.all([loadRows(), loadDetail(selectedId)]);
    } catch (err) {
      setError(resolveErrorMessage(err, "Unable to update verification."));
      toast.error(resolveErrorMessage(err, "Unable to update verification."));
    } finally {
      setActionLoading(false);
    }
  };

  const selectedStatus = String(selectedRecord?.status || "").toLowerCase();
  const canTakeDecision = selectedStatus === "submitted";

  const pendingCount = useMemo(
    () => rows.filter((row) => String(row?.status || "").toLowerCase() === "submitted").length,
    [rows]
  );

  return (
    <div className="card card-custom">
      <div className="card-header flex-wrap border-0 pt-6 pb-0">
        <div className="card-title">
          <h3 className="card-label">Enterprise Verifications</h3>
        </div>
        <div className="card-toolbar text-muted">
          Submitted pending: <strong className="ml-1">{pendingCount}</strong>
        </div>
      </div>

      <div className="card-body pt-4">
        <div className="row mb-4">
          <div className="col-md-6 mb-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search enterprise, user, email, status"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="col-md-3 mb-2">
            <select
              className="form-control"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3 mb-2 text-right">
            <button type="button" className="btn btn-light-primary" onClick={loadRows}>
              Refresh
            </button>
          </div>
        </div>

        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <div className="table-responsive mb-4">
          <table className="table table-hover table-head-custom table-vertical-center">
            <thead>
              <tr>
                <th>Enterprise</th>
                <th>Lead</th>
                <th>User</th>
                <th>Status</th>
                <th>Submitted At</th>
                <th>Reviewed At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>Loading...</td>
                </tr>
              ) : null}

              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>No verification records found.</td>
                </tr>
              ) : null}

              {!loading
                ? rows.map((row) => {
                    const isActive = selectedId === row?._id;
                    return (
                      <tr key={row?._id} className={isActive ? "table-primary" : ""}>
                        <td>{row?.enterprise?.name || row?.enterprise?.companyName || "-"}</td>
                        <td>
                          <div>{row?.lead?.name || "-"}</div>
                          <div className="text-muted">{row?.lead?.email || "-"}</div>
                        </td>
                        <td>
                          <div>{row?.user?.name || "Unknown"}</div>
                          <div className="text-muted">{row?.user?.email || "-"}</div>
                        </td>
                        <td>{toDisplayStatus(row?.status)}</td>
                        <td>{toDateTime(row?.submittedAt)}</td>
                        <td>{toDateTime(row?.approvedAt || row?.rejectedAt)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-light-info"
                            onClick={() => loadDetail(row?._id)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                : null}
            </tbody>
          </table>
        </div>

        <div className="card card-custom card-stretch">
          <div className="card-header">
            <div className="card-title">
              <h4 className="card-label mb-0">Verification Detail</h4>
            </div>
          </div>
          <div className="card-body">
            {detailLoading ? <div>Loading details...</div> : null}
            {!detailLoading && !selectedRecord ? (
              <div className="text-muted">Select a verification record to inspect captures and decision timeline.</div>
            ) : null}

            {!detailLoading && selectedRecord ? (
              <>
                <div className="alert alert-custom alert-light-primary py-3 mb-4">
                  <div className="d-flex flex-wrap">
                    <div className="mr-8 mb-2">
                      <div className="font-weight-bold text-dark-75">Enterprise</div>
                      <div className="text-dark">{selectedRecord?.enterprise?.name || "-"}</div>
                    </div>
                    <div className="mr-8 mb-2">
                      <div className="font-weight-bold text-dark-75">Lead</div>
                      <div className="text-dark">{selectedRecord?.lead?.name || "-"}</div>
                      <div className="text-muted">{selectedRecord?.lead?.email || "-"}</div>
                    </div>
                    <div className="mb-2">
                      <div className="font-weight-bold text-dark-75">Participant</div>
                      <div className="text-dark">{selectedRecord?.user?.name || "-"}</div>
                      <div className="text-muted">{selectedRecord?.user?.email || "-"}</div>
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="font-weight-bold">Linked Profile</div>
                    <div>
                      {selectedRecord?.linkedProfile ? (
                        isLikelyUrl(selectedRecord.linkedProfile) ? (
                          <a
                            href={selectedRecord.linkedProfile}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {selectedRecord.linkedProfile}
                          </a>
                        ) : (
                          selectedRecord.linkedProfile
                        )
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="font-weight-bold">Country</div>
                    <div>{selectedRecord?.country || "-"}</div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="font-weight-bold">Status</div>
                    <div>{toDisplayStatus(selectedRecord?.status)}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="font-weight-bold">Submitted At</div>
                    <div>{toDateTime(selectedRecord?.submittedAt)}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="font-weight-bold">Approved At</div>
                    <div>{toDateTime(selectedRecord?.approvedAt)}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="font-weight-bold">Rejected At</div>
                    <div>{toDateTime(selectedRecord?.rejectedAt)}</div>
                  </div>
                  <div className="col-md-3">
                    <div className="font-weight-bold">Skipped At</div>
                    <div>{toDateTime(selectedRecord?.skippedAt)}</div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-4 mb-2">
                    <div className="font-weight-bold mb-2">Live Photo</div>
                    {selectedRecord?.assets?.livePhoto?.url ? (
                      <img
                        src={selectedRecord.assets.livePhoto.url}
                        alt="Live photo"
                        onClick={() =>
                          setImagePreview({
                            label: "Live Photo",
                            url: selectedRecord.assets.livePhoto.url,
                          })
                        }
                        style={{
                          width: "100%",
                          maxHeight: "220px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          border: "1px solid #dfe3eb",
                          cursor: "zoom-in",
                        }}
                      />
                    ) : (
                      <div className="text-muted">Not uploaded</div>
                    )}
                  </div>
                  <div className="col-md-4 mb-2">
                    <div className="font-weight-bold mb-2">Document Front</div>
                    {selectedRecord?.assets?.documentFront?.url ? (
                      <img
                        src={selectedRecord.assets.documentFront.url}
                        alt="Document front"
                        onClick={() =>
                          setImagePreview({
                            label: "Document Front",
                            url: selectedRecord.assets.documentFront.url,
                          })
                        }
                        style={{
                          width: "100%",
                          maxHeight: "220px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          border: "1px solid #dfe3eb",
                          cursor: "zoom-in",
                        }}
                      />
                    ) : (
                      <div className="text-muted">Not uploaded</div>
                    )}
                  </div>
                  <div className="col-md-4 mb-2">
                    <div className="font-weight-bold mb-2">Document Back</div>
                    {selectedRecord?.assets?.documentBack?.url ? (
                      <img
                        src={selectedRecord.assets.documentBack.url}
                        alt="Document back"
                        onClick={() =>
                          setImagePreview({
                            label: "Document Back",
                            url: selectedRecord.assets.documentBack.url,
                          })
                        }
                        style={{
                          width: "100%",
                          maxHeight: "220px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          border: "1px solid #dfe3eb",
                          cursor: "zoom-in",
                        }}
                      />
                    ) : (
                      <div className="text-muted">Not uploaded</div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Rejection Reason</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter rejection reason"
                    value={rejectionReason}
                    onChange={(event) => setRejectionReason(event.target.value)}
                    disabled={actionLoading || !canTakeDecision}
                  />
                  {selectedRecord?.review?.rejectionReason ? (
                    <small className="form-text text-muted">
                      Existing reason: {selectedRecord.review.rejectionReason}
                    </small>
                  ) : null}
                </div>

                <div className="d-flex mt-3">
                  <button
                    type="button"
                    className="btn btn-success mr-3"
                    disabled={actionLoading || !canTakeDecision}
                    onClick={() => handleAction("approve")}
                  >
                    {actionLoading ? "Please wait..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={actionLoading || !canTakeDecision}
                    onClick={() => handleAction("reject")}
                  >
                    {actionLoading ? "Please wait..." : "Reject"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
        {imagePreview?.url ? (
          <div
            role="presentation"
            onClick={() => setImagePreview(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.7)",
              zIndex: 1200,
              padding: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              role="presentation"
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "min(980px, 94vw)",
                maxHeight: "92vh",
                background: "#fff",
                borderRadius: "12px",
                padding: "1rem",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-3">
                <strong>{imagePreview.label}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setImagePreview(null)}
                >
                  Close
                </button>
              </div>
              <img
                src={imagePreview.url}
                alt={imagePreview.label}
                style={{
                  width: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                  border: "1px solid #e4e8f0",
                  background: "#f8fafc",
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EnterpriseVerifications;
